import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import api, { openSafeTelegramLink, isSafeTelegramUrl } from '../api/client';
import { CoinIcon, FloatIcon, RubyIcon, ScreenTitle } from './CurrencyIcons';
import AlertModal, { useViewportCenterStyle } from './AlertModal';
import Avatar from './Avatar';
import IconImg from './IconImg';
import { iconUrl } from '../utils/assets';
import { spawnTapFxBurst } from '../utils/tapFx';
import { planetOrbit, planetFxOverlay } from '../utils/planetOrbit';
import { useLocale } from '../i18n/LocaleContext';
import { useGameAlert } from '../hooks/useGameAlert';
import { mergeClickerState } from '../utils/clickerMerge';
import { awaitDrainPendingTaps } from '../utils/tapFlush';

function inactiveLabel(m, t) {
  if (m.active_today) return t('inactiveToday');
  if (m.inactive_days == null) return t('inactiveNever');
  if (m.inactive_days <= 0) return t('inactiveToday');
  return t('inactiveDays', { n: m.inactive_days });
}

const SHOP_SLOTS = [
  { id: 'frame', labelKey: 'slot_frame', icon: 'gem.png' },
  { id: 'planet', labelKey: 'slot_planet', icon: 'fire.png' },
  { id: 'title', labelKey: 'slot_title', icon: 'handshake.png' },
  { id: 'tap_fx', labelKey: 'slot_tap_fx', icon: 'muscle.png' },
];

const RARITY_WEIGHT = { common: 0, rare: 1, epic: 2, legendary: 3, mythic: 4 };

const cosAsset = (item) => (item?.asset ? item.asset : null);

/** Orbit deco (ring / disk) — never receives taps; sphere stays separately clickable. */
const PlanetOrbitDeco = ({ code, layer, small = false }) => {
  const orbit = planetOrbit(code);
  if (!orbit) return null;
  const src = layer === 'front' ? orbit.front : orbit.back;
  return (
    <span
      className={`planet-orbit planet-orbit-${layer} orbit-${orbit.kind}${orbit.spin ? ' is-spin' : ''}`}
      aria-hidden
    >
      <span className="planet-orbit-spin">
        <IconImg className="planet-deco" src={src} small={small} draggable={false} decoding="async" />
      </span>
    </span>
  );
};

/**
 * Live planet cosmetic: full-size sphere + optional orbit layers around it.
 *
 * Atelier grids show dozens of these at 64-92px, so anything card-sized takes
 * the thumbnail cut instead of the 1024px sphere the tap screen needs.
 */
const PlanetArt = ({ code, src, size = 64, live = true }) => {
  const orbit = planetOrbit(code);
  const fxOverlay = planetFxOverlay(code);
  const small = size <= 128;
  return (
    <span
      className={`atl-planet-art planet-${code}${src ? ' has-asset' : ''}${live ? ' is-live' : ''}${orbit ? ' has-orbit' : ''}`}
      style={{ width: size, height: size }}
    >
      {orbit ? <PlanetOrbitDeco code={code} layer="back" small={small} /> : null}
      {src ? (
        <IconImg
          className="atl-planet-img"
          src={src}
          small={small}
          draggable={false}
          decoding="async"
        />
      ) : (
        <>
          <span className="atl-planet-body" />
          <span className="atl-planet-atmo" aria-hidden />
        </>
      )}
      {orbit ? <PlanetOrbitDeco code={code} layer="front" small={small} /> : null}
      <span className={`planet-fx planet-fx-${code}`} aria-hidden>
        {fxOverlay ? <IconImg src={fxOverlay} small={small} draggable={false} decoding="async" /> : null}
      </span>
    </span>
  );
};

const CosmeticArt = ({ item, userData, size = 64, label }) => {
  if (item.slot === 'frame') {
    return (
      <span
        className={`profile-avatar-wrap shop-swatch-avatar frame-${item.code}`}
        style={{ '--av': `${size}px` }}
      >
        <Avatar userData={userData} size={size} />
      </span>
    );
  }
  if (item.slot === 'planet') {
    return <PlanetArt code={item.code} src={cosAsset(item)} size={size} />;
  }
  if (item.slot === 'tap_fx') {
    return <TapFxCardArt code={item.code} size={size} />;
  }
  if (item.slot === 'title') {
    return (
      <span className={`atl-title-art title-${item.code}`}>
        <span className="atl-title-chip">{label || item.name}</span>
      </span>
    );
  }
  return (
    <span className={`atl-title-art title-${item.code}`}>
      <span className="atl-title-chip">{label || item.name}</span>
    </span>
  );
};

/** Frozen «best frame» of a tap burst — static, centered, varied particle rotation. */
const TapFxCardArt = ({ code, size = 64 }) => {
  const profile = { count: 8, ...({ tap_fx_sparks: { count: 8 }, tap_fx_ember: { count: 9 }, tap_fx_ice: { count: 10 }, tap_fx_nova: { count: 12 }, tap_fx_dust: { count: 11 }, tap_fx_shards: { count: 9 }, tap_fx_plasma: { count: 8 }, tap_fx_supernova: { count: 14 }, tap_fx_meteor: { count: 6 }, tap_fx_shockwave: { count: 5 } }[code] || {}) };
  const scale = Math.max(0.5, size / 100);
  const sparks = Array.from({ length: profile.count }, (_, i) => {
    const angle = (Math.PI * 2 * i) / profile.count - Math.PI / 2;
    const dist = (18 + (i % 3) * 7) * scale;
    const rot = (i * 47 + 18) % 360;
    return {
      i,
      sx: Math.cos(angle) * dist,
      sy: Math.sin(angle) * dist,
      rot,
    };
  });
  return (
    <span className={`atl-fx-art atl-fx-static fx-${code}`} style={{ width: size, height: size }} aria-hidden>
      {sparks.map((s) => (
        <i
          key={s.i}
          className={`tap-spark is-frozen fx-${code}`}
          style={{
            left: '50%',
            top: '50%',
            margin: 0,
            animation: 'none',
            opacity: 0.92,
            transform: `translate(-50%, -50%) translate(${s.sx}px, ${s.sy}px) rotate(${s.rot}deg)`,
          }}
        />
      ))}
    </span>
  );
};

/** Preview that repeatedly fires particle bursts like a real tap (no planet). */
const TapFxDemo = ({ code }) => {
  const stageRef = useRef(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    let alive = true;

    // `is-press` only styles a `.preview-fx-btn` child, and this stage is the
    // `preview-fx-bare` variant which has none — so the class toggle did
    // nothing, and the `offsetWidth` read that went with it was a full forced
    // layout of the page every 900 ms for no picture at all.
    const burst = () => {
      if (!alive) return;
      if (document.visibilityState !== 'visible') return;
      spawnTapFxBurst(stage, code, { scale: 1.15 });
    };

    burst();
    const id = window.setInterval(burst, 900);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [code]);

  return <div className="preview-fx-live preview-fx-bare" ref={stageRef} />;
};

const PreviewBody = ({ item, userData, itemName, previewText, t }) => {
  const name = itemName(item);
  const hint = previewText(item);
  const tag = t(SHOP_SLOTS.find((s) => s.id === item.slot)?.labelKey || 'shop');

  if (item.slot === 'frame') {
    return (
      <>
        <span className="preview-slot-tag">{tag}</span>
        <div className="frame-preview-block">
          <div
            className={`profile-avatar-wrap frame-preview-avatar frame-${item.code}`}
            style={{ '--av': '84px' }}
          >
            <Avatar userData={userData} size={84} />
          </div>
        </div>
        <p className="preview-hint">{hint}</p>
      </>
    );
  }

  if (item.slot === 'planet') {
    const src = cosAsset(item) || 'button.png';
    const orbit = planetOrbit(item.code);
    return (
      <>
        <span className="preview-slot-tag">{tag}</span>
        <div className="preview-stage-wrap">
          <div className={`tap-stage preview-stage planet-${item.code}${orbit ? ' has-orbit' : ''}`}>
            <div className="tap-aura" aria-hidden />
            {orbit ? <PlanetOrbitDeco code={item.code} layer="back" /> : null}
            <div className="tap-button preview-btn has-asset">
              <IconImg
                className="tap-planet-img"
                src={src}
                draggable={false}
                decoding="async"
              />
              <span className={`planet-fx planet-fx-${item.code}`} aria-hidden>
                {planetFxOverlay(item.code) ? (
                  <IconImg src={planetFxOverlay(item.code)} draggable={false} decoding="async" />
                ) : null}
              </span>
            </div>
            {orbit ? <PlanetOrbitDeco code={item.code} layer="front" /> : null}
          </div>
        </div>
        <p className="preview-hint">{hint}</p>
      </>
    );
  }

  if (item.slot === 'title') {
    return (
      <>
        <span className="preview-slot-tag">{tag}</span>
        <div className="preview-title-mock">
          <div
            className="profile-avatar-wrap frame-preview-avatar frame-none"
            style={{ '--av': '44px' }}
          >
            <Avatar userData={userData} size={44} />
          </div>
          <div className="preview-title-text">
            <div className="preview-name">{userData?.first_name || userData?.username || 'Commander'}</div>
            <span className={`atl-title-chip title-${item.code}`}>{name}</span>
          </div>
        </div>
        <p className="preview-hint">{hint}</p>
      </>
    );
  }

  return (
    <>
      <span className="preview-slot-tag">{tag}</span>
      <TapFxDemo code={item.code} />
      <p className="preview-hint">{hint}</p>
    </>
  );
};

const Hub = ({ onClickerUpdate, clickerData, userData }) => {
  const { t, lang } = useLocale();
  const { alert, closeAlert, confirmAlert, showError, showInfo, showConfirm } = useGameAlert();
  const previewViewportStyle = useViewportCenterStyle();
  const [tab, setTab] = useState('shop');
  const [shopSlot, setShopSlot] = useState('frame');
  const [cosmetics, setCosmetics] = useState(null);
  const [clans, setClans] = useState(null);
  const [prestige, setPrestige] = useState(null);
  const [refs, setRefs] = useState(null);
  const [clanForm, setClanForm] = useState({ name: '', tag: '', description: '', is_open: true });
  const [inviteCode, setInviteCode] = useState('');
  const [previewId, setPreviewId] = useState(null);
  const [clanSheet, setClanSheet] = useState(null); // 'create' | 'join' | null
  const [clanBusy, setClanBusy] = useState(false);
  const [shopBusy, setShopBusy] = useState(null);
  const [clanDescDraft, setClanDescDraft] = useState('');
  const [clanDescReady, setClanDescReady] = useState(false);

  useEffect(() => {
    if (clans?.my_clan) {
      setClanDescDraft(clans.my_clan.description || '');
      setClanDescReady(true);
    } else {
      setClanDescDraft('');
      setClanDescReady(false);
    }
  }, [clans]);

  const TABS = [
    { id: 'shop', label: t('shop'), icon: 'gem.png' },
    { id: 'clans', label: t('clans'), icon: 'handshake.png' },
    { id: 'prestige', label: t('prestige'), icon: 'glowing_star.png' },
  ];

  const load = useCallback(async () => {
    try {
      const [c, cl, p, r] = await Promise.all([
        api.cosmetics(),
        api.clans(),
        api.prestige(),
        api.referrals(),
      ]);
      setCosmetics(c);
      setClans(cl);
      setPrestige(p);
      setRefs(r);
    } catch (err) {
      showError(err);
    }
  }, [showError]);

  useEffect(() => {
    load();
  }, [load]);

  const shopItems = useMemo(() => {
    const items = (cosmetics?.items || []).filter(
      (i) => i.amber_price > 0 && !i.season_only
    );
    // Rarest and priciest first — the shelf should open on the trophies, not on
    // the commons. Stable ties by id so the order never jitters between loads.
    return items
      .filter((i) => i.slot === shopSlot)
      .sort((a, b) => {
        const wa = RARITY_WEIGHT[a.rarity] ?? 0;
        const wb = RARITY_WEIGHT[b.rarity] ?? 0;
        if (wa !== wb) return wb - wa;
        if (b.amber_price !== a.amber_price) return b.amber_price - a.amber_price;
        return a.id - b.id;
      });
  }, [cosmetics, shopSlot]);

  /** Equal title plaque width = longest title in the active shelf (chars → px). */
  const titleArtWidth = useMemo(() => {
    if (shopSlot !== 'title') return null;
    const names = shopItems.map((i) => (lang === 'en' ? i.name_en || i.name : i.name));
    const maxChars = Math.max(8, ...names.map((n) => String(n || '').length));
    // ~7.2px per uppercase glyph at chip size + padding — clamp so cards stay usable
    return Math.min(168, Math.max(96, Math.ceil(maxChars * 7.2 + 20)));
  }, [shopItems, shopSlot, lang]);

  /** Per-slot completion — drives the category tiles and the collection bar. */
  const slotCounts = useMemo(() => {
    const items = (cosmetics?.items || []).filter((i) => i.amber_price > 0 && !i.season_only);
    const map = {};
    SHOP_SLOTS.forEach((s) => {
      const inSlot = items.filter((i) => i.slot === s.id);
      map[s.id] = { total: inSlot.length, owned: inSlot.filter((i) => i.owned).length };
    });
    return map;
  }, [cosmetics]);

  const collection = useMemo(() => {
    const items = (cosmetics?.items || []).filter((i) => i.amber_price > 0 && !i.season_only);
    const owned = items.filter((i) => i.owned).length;
    return {
      owned,
      total: items.length,
      pct: items.length ? Math.round((owned / items.length) * 100) : 0,
    };
  }, [cosmetics]);

  /**
   * The single most aspirational unowned item in the active category — shown as
   * a hero card so the shelf has a clear "top of the ladder" instead of a
   * uniform list where nothing stands out.
   */
  const spotlight = useMemo(() => {
    const candidates = shopItems.filter((i) => !i.owned);
    if (!candidates.length) return null;
    const best = candidates.reduce((a, b) => {
      const wa = RARITY_WEIGHT[a.rarity] ?? 0;
      const wb = RARITY_WEIGHT[b.rarity] ?? 0;
      if (wa !== wb) return wb > wa ? b : a;
      return b.amber_price > a.amber_price ? b : a;
    });
    // Only headline something genuinely premium — otherwise it's just noise.
    return (RARITY_WEIGHT[best.rarity] ?? 0) >= 3 ? best : null;
  }, [shopItems]);

  const seasonOwned = useMemo(
    () => (cosmetics?.items || []).filter((i) => i.season_only && i.owned),
    [cosmetics]
  );

  const previewItem = useMemo(
    () => (cosmetics?.items || []).find((i) => i.id === previewId) || null,
    [cosmetics, previewId]
  );

  const amberNow = Number(clickerData?.amber ?? cosmetics?.amber ?? clans?.amber ?? 0);

  const pushAmber = (amber, extraClicker = {}) => {
    const n = Number(amber);
    if (!Number.isFinite(n)) return;
    const stamp = Math.floor(Date.now() / 1000);
    setCosmetics((prev) => (prev ? { ...prev, amber: n } : prev));
    setClans((prev) => (prev ? { ...prev, amber: n } : prev));
    onClickerUpdate?.((prev) =>
      mergeClickerState(prev, {
        amber: n,
        amber_updated_at: Math.max(stamp, Number(prev?.amber_updated_at) || 0),
        ...extraClicker,
      })
    );
  };

  /** Merge server sync — prefer fresher amber + server engagement when present. */
  const mergeClicker = (synced, extra = {}) => {
    if (!synced) return;
    onClickerUpdate?.((prev) =>
      mergeClickerState(prev, synced, { preferRemoteBalance: true, ...extra })
    );
  };

  const itemName = (item) => (lang === 'en' ? item.name_en || item.name : item.name);
  const itemDesc = (item) =>
    lang === 'en' ? item.description_en || item.description : item.description;

  const buy = async (id) => {
    if (shopBusy != null) return false;
    setShopBusy(id);
    try {
      const drained = await awaitDrainPendingTaps();
      if (drained.last) mergeClicker(drained.last);
      if (!drained.ok) {
        showError({ message: lang === 'en' ? 'Still syncing taps — try again' : 'Тапы ещё синхронизируются — попробуй ещё раз' });
        return false;
      }
      const result = await api.cosmeticsBuy(id);
      setCosmetics(result);
      if (result.clicker) {
        mergeClicker(result.clicker, { preferRemoteBalance: true });
        if (result.amber != null || result.clicker.amber != null) {
          pushAmber(result.clicker.amber ?? result.amber);
        }
      } else {
        pushAmber(result.amber);
        const synced = await api.clickerSync();
        mergeClicker(synced, { preferRemoteBalance: true, amber: synced.amber ?? result.amber });
        if (synced?.amber != null) pushAmber(synced.amber);
      }
      showInfo({ icon: 'gem.png', title: t('boughtRubies'), confirmLabel: t('gotIt') });
      return true;
    } catch (err) {
      showError(err);
      return false;
    } finally {
      setShopBusy(null);
    }
  };

  /**
   * Buy flow: confirm (price is non-refundable) → purchase → equip immediately.
   * Equipping straight away is the whole point — the player should see what
   * they paid for without hunting for a second button.
   */
  const confirmBuy = async (item) => {
    if (shopBusy != null) return;
    const ok = await showConfirm({
      icon: 'gem.png',
      title: t('buyConfirmTitle', { n: itemName(item) }),
      body: t('buyConfirmBody', { p: item.amber_price }),
      confirmLabel: t('buyAction'),
      cancelLabel: t('close'),
    });
    if (!ok) return;
    const bought = await buy(item.id);
    if (bought) {
      await equip(item.id);
      try {
        const { celebrate } = await import('../utils/fx');
        celebrate('level');
      } catch (_) {
        /* ignore */
      }
    }
  };

  const equip = async (id) => {
    if (shopBusy != null) return false;
    setShopBusy(id);
    try {
      setCosmetics(await api.cosmeticsEquip(id));
      mergeClicker(await api.clickerSync());
      return true;
    } catch (err) {
      showError(err);
      return false;
    } finally {
      setShopBusy(null);
    }
  };

  const unequip = async (id) => {
    if (shopBusy != null) return false;
    setShopBusy(id);
    try {
      setCosmetics(await api.cosmeticsUnequip(id));
      mergeClicker(await api.clickerSync());
      return true;
    } catch (err) {
      showError(err);
      return false;
    } finally {
      // Without this the shop stays locked forever after the first unequip.
      setShopBusy(null);
    }
  };

  const doPrestige = async () => {
    if (shopBusy != null) return;
    const ok = await showConfirm({
      icon: 'glowing_star.png',
      title: t('doPrestige'),
      body: t('prestigeConfirm'),
      confirmLabel: t('doPrestige'),
      cancelLabel: t('close'),
      destructive: true,
    });
    if (!ok) return;
    setShopBusy('prestige');
    try {
      const drained = await awaitDrainPendingTaps();
      if (drained.last) mergeClicker(drained.last);
      if (!drained.ok) {
        showError({ message: lang === 'en' ? 'Still syncing taps — try again' : 'Тапы ещё синхронизируются — попробуй ещё раз' });
        return;
      }
      setPrestige(await api.doPrestige());
      mergeClicker(await api.clickerSync());
      try {
        const { celebrate } = await import('../utils/fx');
        celebrate('league');
      } catch (_) {
        /* ignore */
      }
      showInfo({ icon: 'glowing_star.png', title: t('prestigeDone'), confirmLabel: t('gotIt') });
    } catch (err) {
      showError(err);
    } finally {
      setShopBusy(null);
    }
  };

  const previewText = (item) =>
    lang === 'en' ? item.preview_en || item.preview || item.description : item.preview || item.description;

  /**
   * Real scarcity, straight from the server (`owners` / `owners_pct`): how much
   * of the colony actually walks around with this skin. Nothing sells a premium
   * item like «0.4% игроков». Silently hidden if the backend is older and does
   * not send the numbers.
   */
  const scarcityText = (item) => {
    const owners = Number(item?.owners);
    const pct = Number(item?.owners_pct);
    if (!Number.isFinite(owners) || !Number.isFinite(pct)) return null;
    if (owners <= 0) return t('ownersNone');
    if (pct < 0.1) return t('ownersFew', { n: owners });
    return t('ownersPct', { p: pct < 10 ? pct.toFixed(1) : String(Math.round(pct)) });
  };

  /** Rarity as pips (1–5) — instant "how high up the ladder is this" read. */
  const RarityPips = ({ rarity }) => {
    const lvl = (RARITY_WEIGHT[rarity] ?? 0) + 1;
    return (
      <span className="atl-pips" aria-hidden>
        {[0, 1, 2, 3, 4].map((i) => (
          <i key={i} className={i < lvl ? 'is-on' : ''} />
        ))}
      </span>
    );
  };

  return (
    <main className={`tasks-screen screen-enter ${tab === 'clans' ? 'hub-clans-mode' : ''}`}>
      {tab !== 'clans' ? (
        <ScreenTitle icon={iconUrl('folder.png')} title={t('hubTitle')} subtitle={t('hubSubtitle')}>
          <span className="amber-balance hub-amber">
            <RubyIcon size={18} />
            {amberNow}
          </span>
        </ScreenTitle>
      ) : (
        <div className="clan-screen-header">
          <div className="clan-screen-title-row">
            <FloatIcon src={iconUrl('handshake.png')} size={28} eager animated native />
            <div>
              <div className="upgrades-item-info-name">{t('clans')}</div>
              <div className="item-desc">{t('hubSubtitle')}</div>
            </div>
            <span className="amber-balance hub-amber">
              <RubyIcon size={18} />
              {amberNow}
            </span>
          </div>
        </div>
      )}

      <div className="hub-tabs hub-tabs-3" role="tablist">
        {TABS.map(({ id, label, icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`hub-tab ${tab === id ? 'is-active' : ''}`}
            onClick={() => setTab(id)}
          >
            <FloatIcon src={iconUrl(icon)} size={22} animated={tab === id} eager native />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {tab !== 'clans' ? (
        <>
          <Link to="/clicker/duel" className="referrals-bar duel-entry-bar glass-panel">
            <FloatIcon src={iconUrl('muscle.png')} size={28} eager animated native />
            <div className="referrals-bar-text">
              <span className="referrals-bar-title">{t('duelsTitle')}</span>
              <span className="referrals-bar-sub">{t('duelsEntryHint')}</span>
            </div>
            <span className="referrals-bar-count duel-entry-go">→</span>
          </Link>
          <Link to="/clicker/referals" className="referrals-bar glass-panel">
            <FloatIcon src={iconUrl('handshake.png')} size={28} eager animated native />
            <div className="referrals-bar-text">
              <span className="referrals-bar-title">{t('referralsBar')}</span>
              <span className="referrals-bar-sub">{t('inviteFriends')}</span>
            </div>
            <span className="referrals-bar-count">{refs?.invite_count ?? 0}</span>
          </Link>
        </>
      ) : null}

      {tab === 'shop' && cosmetics ? (
        <div className="atelier upgrades-list">
          <div className="atl-hero">
            <div className="atl-hero-copy">
              <div className="atl-hero-kicker">{t('atelierKicker')}</div>
              <div className="atl-hero-title">{t('atelierTitle')}</div>
              <div className="atl-hero-sub">{t('atelierHint')}</div>
            </div>
            <div className="atl-hero-wallet">
              <span className="atl-hero-gem" aria-hidden>
                <RubyIcon size={30} />
              </span>
              <span className="atl-hero-wallet-text">
                <span className="atl-hero-amount">{amberNow}</span>
                <span className="atl-hero-wallet-label">{t('rubies')}</span>
              </span>
            </div>
            <div className="atl-hero-glow" aria-hidden />
            <span className="atl-hero-sheen" aria-hidden />
          </div>

          <div className="atl-collection-bar">
            <div className="atl-collection-track">
              <i style={{ width: `${collection.pct}%` }} />
            </div>
            <span className="atl-collection-text">
              {t('ownedCount', { n: collection.owned, b: collection.total })}
            </span>
          </div>

          <div className="atl-cats" role="tablist">
            {SHOP_SLOTS.map((s) => {
              const c = slotCounts[s.id] || { owned: 0, total: 0 };
              const done = c.total > 0 && c.owned === c.total;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={shopSlot === s.id}
                  className={`atl-cat cat-${s.id} ${shopSlot === s.id ? 'is-active' : ''} ${done ? 'is-done' : ''}`}
                  onClick={() => setShopSlot(s.id)}
                >
                  <span className="atl-cat-icon" aria-hidden>
                    <FloatIcon src={iconUrl(s.icon)} size={26} eager animated={shopSlot === s.id} native />
                  </span>
                  <span className="atl-cat-label">{t(s.labelKey)}</span>
                  <span className="atl-cat-count">
                    {c.owned}<i>/{c.total}</i>
                  </span>
                  <span className="atl-cat-track" aria-hidden>
                    <i style={{ width: c.total ? `${(c.owned / c.total) * 100}%` : '0%' }} />
                  </span>
                </button>
              );
            })}
          </div>
          <p className="atl-slot-hint">{t(`slotHint_${shopSlot}`)}</p>

          {spotlight ? (
            <article
              className={`atl-spotlight rarity-${spotlight.rarity}`}
              key={`sp-${spotlight.id}`}
            >
              <span className="atl-spot-rays" aria-hidden />
              <div className="atl-spot-badge">
                <span className="atl-spot-star" aria-hidden>★</span>
                {t('atelierSpotlight')}
              </div>
              <button
                type="button"
                className="atl-spot-art"
                onClick={() => setPreviewId(spotlight.id)}
                aria-label={itemName(spotlight)}
              >
                <span className="atl-art-halo" aria-hidden />
                <span className="atl-art-ring" aria-hidden />
                <CosmeticArt item={spotlight} userData={userData} size={92} label={itemName(spotlight)} />
                <span className="atl-spot-pedestal" aria-hidden />
              </button>
              <div className="atl-spot-body">
                <div className="atl-info-top">
                  <span className={`atl-rarity rarity-${spotlight.rarity}`}>
                    {t(`rarity_${spotlight.rarity}`)}
                  </span>
                  <RarityPips rarity={spotlight.rarity} />
                </div>
                <h3 className="atl-spot-name">{itemName(spotlight)}</h3>
                <p className="atl-spot-desc">{itemDesc(spotlight) || previewText(spotlight)}</p>
                {scarcityText(spotlight) ? (
                  <span className="atl-scarcity is-hero">
                    <i aria-hidden />
                    {scarcityText(spotlight)}
                  </span>
                ) : null}
                <div className="atl-spot-actions">
                  <button
                    type="button"
                    className="atl-btn atl-btn-ghost"
                    onClick={() => setPreviewId(spotlight.id)}
                  >
                    {t('tryPreview')}
                  </button>
                  <button
                    type="button"
                    className="atl-btn atl-btn-buy"
                    disabled={shopBusy != null || amberNow < spotlight.amber_price}
                    onClick={() => confirmBuy(spotlight)}
                  >
                    <RubyIcon size={15} />
                    {spotlight.amber_price}
                  </button>
                </div>
              </div>
            </article>
          ) : null}

          <div className="atl-catalog">
            <div className="atl-catalog-head">
              <span className="atl-catalog-title">
                {t(SHOP_SLOTS.find((s) => s.id === shopSlot)?.labelKey || 'shop')}
              </span>
              {slotCounts[shopSlot]?.total &&
              slotCounts[shopSlot].owned === slotCounts[shopSlot].total ? (
                <span className="atl-catalog-done">{t('slotDone')}</span>
              ) : (
                <span className="atl-catalog-sub">
                  {t('collectionProgress', {
                    a: slotCounts[shopSlot]?.owned || 0,
                    b: slotCounts[shopSlot]?.total || 0,
                  })}
                </span>
              )}
            </div>

            <div
              className={`atl-grid${shopSlot === 'title' ? ' is-title-grid' : ''}`}
              style={titleArtWidth ? { '--title-art-w': `${titleArtWidth}px` } : undefined}
            >
              {shopItems.map((item) => {
                const affordable = amberNow >= item.amber_price;
                const pct = item.amber_price
                  ? Math.max(0, Math.min(100, (amberNow / item.amber_price) * 100))
                  : 100;
                const scarce = scarcityText(item);
                const top = (RARITY_WEIGHT[item.rarity] ?? 0) >= 3;
                return (
                  <article
                    key={item.id}
                    className={`atl-card rarity-${item.rarity} slot-${item.slot} ${item.owned ? 'is-owned' : ''} ${
                      item.equipped ? 'is-equipped' : ''
                    } ${!item.owned && !affordable ? 'is-locked' : ''} ${top ? 'is-top' : ''}`}
                  >
                    <span className="atl-card-foil" aria-hidden />
                    <span className="atl-card-edge" aria-hidden />
                    {top ? (
                      <span className="atl-card-ribbon" aria-hidden>
                        {t(`rarity_${item.rarity}`)}
                      </span>
                    ) : null}

                    <button
                      type="button"
                      className="atl-art"
                      onClick={() => setPreviewId(item.id)}
                      aria-label={itemName(item)}
                    >
                      <span className="atl-art-halo" aria-hidden />
                      <span className="atl-art-ring" aria-hidden />
                      <CosmeticArt item={item} userData={userData} size={64} label={itemName(item)} />
                      <span className="atl-shine" aria-hidden />
                    </button>

                    <div className="atl-info">
                      <div className="atl-info-top">
                        <span className={`atl-rarity rarity-${item.rarity}`}>
                          {t(`rarity_${item.rarity}`)}
                        </span>
                        <RarityPips rarity={item.rarity} />
                        {item.equipped ? (
                          <span className="atl-state is-on">{t('equippedTag')}</span>
                        ) : item.owned ? (
                          <span className="atl-state">{t('ownedTag')}</span>
                        ) : null}
                      </div>
                      <h3 className="atl-name">{itemName(item)}</h3>
                      <p className="atl-desc">{itemDesc(item) || previewText(item)}</p>
                      {scarce ? (
                        <span className="atl-scarcity">
                          <i aria-hidden />
                          {scarce}
                        </span>
                      ) : null}
                    </div>

                    <div className="atl-foot">
                      {item.owned ? (
                        <div className="atl-actions">
                          <button
                            type="button"
                            className="atl-btn atl-btn-ghost"
                            onClick={() => setPreviewId(item.id)}
                          >
                            {t('tryPreview')}
                          </button>
                          <button
                            type="button"
                            className={`atl-btn ${item.equipped ? 'atl-btn-ghost' : 'atl-btn-equip'}`}
                            disabled={shopBusy != null}
                            onClick={() => (item.equipped ? unequip(item.id) : equip(item.id))}
                          >
                            {item.equipped ? t('unequip') : t('equip')}
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="atl-price-block">
                            <span className={`atl-price ${affordable ? 'is-ready' : ''}`}>
                              <RubyIcon size={17} />
                              {item.amber_price}
                            </span>
                            {!affordable ? (
                              <>
                                <span className="atl-price-track" aria-hidden>
                                  <i style={{ width: `${pct}%` }} />
                                </span>
                                <span className="atl-need">
                                  {t('needMore', { n: item.amber_price - amberNow })}
                                </span>
                              </>
                            ) : null}
                          </div>
                          <div className="atl-actions">
                            <button
                              type="button"
                              className="atl-btn atl-btn-ghost"
                              onClick={() => setPreviewId(item.id)}
                            >
                              {t('tryPreview')}
                            </button>
                            <button
                              type="button"
                              className="atl-btn atl-btn-buy"
                              disabled={shopBusy != null || !affordable}
                              onClick={() => confirmBuy(item)}
                            >
                              {t('buyAction')}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </article>
                );
              })}
              {!shopItems.length ? <div className="inline-message">{t('emptyShop')}</div> : null}
            </div>
          </div>

          {seasonOwned.length ? (
            <div className="atl-season">
              <div className="section-label">{t('seasonFrames')}</div>
              <div className="atl-grid">
                {seasonOwned.map((item) => (
                  <article
                    className={`atl-card rarity-${item.rarity} is-owned ${
                      item.equipped ? 'is-equipped' : ''
                    }`}
                    key={`s-${item.id}`}
                  >
                    <div className="atl-art">
                      <CosmeticArt item={item} userData={userData} size={64} label={itemName(item)} />
                      <span className="atl-shine" aria-hidden />
                    </div>
                    <div className="atl-info">
                      <div className="atl-info-top">
                        <span className={`atl-rarity rarity-${item.rarity}`}>
                          {t(`rarity_${item.rarity}`)}
                        </span>
                        <span className="atl-state is-season">{t('seasonOnly')}</span>
                      </div>
                      <h3 className="atl-name">{itemName(item)}</h3>
                      <p className="atl-desc">{itemDesc(item) || previewText(item)}</p>
                    </div>
                    <div className="atl-foot">
                      <div className="atl-actions">
                        <button
                          type="button"
                          className={`atl-btn ${item.equipped ? 'atl-btn-ghost' : 'atl-btn-equip'}`}
                          disabled={shopBusy != null}
                          onClick={() => (item.equipped ? unequip(item.id) : equip(item.id))}
                        >
                          {item.equipped ? t('unequip') : t('equip')}
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ) : null}

          {previewItem
            ? createPortal(
                <div
                  className="skin-preview-modal alert-overlay"
                  role="dialog"
                  aria-modal="true"
                  style={previewViewportStyle}
                >
                  <button
                    type="button"
                    className="daily-backdrop"
                    onClick={() => setPreviewId(null)}
                    aria-label={t('close')}
                  />
                  <div className="skin-preview-card glass-panel soft-card">
                    <div className="upgrades-item-info-name">{t('previewSkin')}</div>
                    <PreviewBody
                      item={previewItem}
                      userData={userData}
                      itemName={itemName}
                      previewText={previewText}
                      t={t}
                    />
                    <div className="item-desc">
                      <strong>{itemName(previewItem)}</strong>
                      {itemDesc(previewItem) ? ` — ${itemDesc(previewItem)}` : ''}
                    </div>
                    <div className="invite-actions clan-actions-wrap">
                      <button type="button" className="action-btn stars soft-btn" onClick={() => setPreviewId(null)}>
                        {t('close')}
                      </button>
                      {previewItem.owned ? (
                        previewItem.equipped ? (
                          <button
                            type="button"
                            className="action-btn soft-btn"
                            onClick={async () => {
                              if (await unequip(previewItem.id)) setPreviewId(null);
                            }}
                          >
                            {t('unequip')}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="action-btn soft-btn"
                            onClick={async () => {
                              if (await equip(previewItem.id)) setPreviewId(null);
                            }}
                          >
                            {t('equip')}
                          </button>
                        )
                      ) : (
                        <button
                          type="button"
                          className="action-btn soft-btn shop-buy-btn"
                          disabled={shopBusy != null || amberNow < previewItem.amber_price}
                          onClick={async () => {
                            setPreviewId(null);
                            await confirmBuy(previewItem);
                          }}
                        >
                          <RubyIcon size={14} /> {previewItem.amber_price}
                        </button>
                      )}
                    </div>
                  </div>
                </div>,
                document.body
              )
            : null}
        </div>
      ) : null}

      {tab === 'clans' && clans ? (
        <div className="upgrades-list clan-screen">
          {clans.my_clan ? (
            <>
              <div className="invite-panel clan-panel soft-card clan-hero">
                <div className="clan-hero-top">
                  <div>
                    <div className="upgrades-item-info-name clan-hero-name">
                      [{clans.my_clan.tag}] {clans.my_clan.name}
                      <span className="level-badge">
                        {clans.my_clan.is_open ? t('clanPublic') : t('clanPrivate')}
                      </span>
                    </div>
                    <div className="item-desc">
                      {t('power')} {clans.my_clan.power} · {clans.my_clan.members_count}/
                      {clans.my_clan.max_members} · {t('active')} {clans.my_clan.active_today} (
                      {Math.round(clans.my_clan.active_ratio * 100)}%)
                    </div>
                  </div>
                </div>
                {clans.my_clan.description ? (
                  <p className="clan-desc">{clans.my_clan.description}</p>
                ) : (
                  <p className="clan-desc is-empty">{t('clanDescEmpty')}</p>
                )}
                {clans.my_clan.role === 'owner' ? (
                  <div className="clan-desc-edit">
                    <input
                      className="hub-input"
                      maxLength={200}
                      placeholder={t('clanDesc')}
                      value={clanDescReady ? clanDescDraft : clans.my_clan.description || ''}
                      onChange={(e) => {
                        setClanDescReady(true);
                        setClanDescDraft(e.target.value);
                      }}
                    />
                    <button
                      type="button"
                      className="action-btn soft-btn"
                      disabled={clanBusy}
                      onClick={async () => {
                        setClanBusy(true);
                        try {
                          const next = clanDescDraft.trim();
                          const res = await api.clanSetDescription(next);
                          setClans(res);
                          setClanDescDraft(res?.my_clan?.description || next);
                          setClanDescReady(true);
                        } catch (e) {
                          showError(e);
                        } finally {
                          setClanBusy(false);
                        }
                      }}
                    >
                      {t('save')}
                    </button>
                  </div>
                ) : null}
                <div className="item-desc">
                  {t('incomeBonus')}:{' '}
                  {clans.my_clan.income_bonus_percent
                    ? `+${clans.my_clan.income_bonus_percent}%`
                    : t('needOnline')}
                </div>
                {clans.my_clan.week_goal ? (
                  <div className="clan-week-goal">
                    <div className="upgrades-item-info-name">{t('clanWeekGoal')}</div>
                    <div className="item-desc">
                      {clans.my_clan.week_goal.progress} / {clans.my_clan.week_goal.target} ·{' '}
                      <RubyIcon size={12} /> {clans.my_clan.week_goal.reward_amber}
                      {clans.my_clan.week_goal.completed ? ` · ${t('done')}` : ''}
                    </div>
                    <div className="bp-xp-bar" aria-hidden>
                      <div
                        className="bp-xp-fill"
                        style={{ transform: `scaleX(${(clans.my_clan.week_goal.pct || 0) / 100})` }}
                      />
                    </div>
                  </div>
                ) : null}
                {(clans.my_clan.role === 'owner' || clans.my_clan.role === 'officer') && (
                  <div className="clan-upgrade-grid">
                    {['slots', 'income', 'power'].map((kind) => {
                      const cost = clans.my_clan.upgrades?.[`next_cost_${kind}`];
                      const lvl = clans.my_clan.upgrades?.[kind] ?? 0;
                      const meta = (clans.upgrade_catalog || []).find((x) => x.id === kind);
                      return (
                        <button
                          key={kind}
                          type="button"
                          className="clan-upgrade-btn soft-card"
                          disabled={cost == null || clanBusy}
                          onClick={async () => {
                            setClanBusy(true);
                            try {
                              const res = await api.clanUpgrade(kind);
                              setClans(res);
                              if (res?.amber != null) pushAmber(res.amber);
                              const synced = await api.clickerSync();
                              mergeClicker(synced);
                              if (synced?.amber != null) pushAmber(synced.amber);
                            } catch (e) {
                              showError(e);
                            } finally {
                              setClanBusy(false);
                            }
                          }}
                        >
                          <span className="upgrades-item-info-name">
                            {lang === 'en' ? meta?.name_en || kind : meta?.name || kind} · {t('levelShort')}
                            {lvl}
                          </span>
                          <span className="item-desc">
                            {lang === 'en' ? meta?.desc_en : meta?.desc}
                          </span>
                          <span className="amber-balance">
                            <RubyIcon size={12} /> {cost ?? t('maxShort')}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {clans.my_clan.role === 'owner' ? (
                  <div className="clan-actions-wrap" style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      className="action-btn soft-btn"
                      onClick={async () => {
                        try {
                          setClans(await api.clanPrivacy(!clans.my_clan.is_open));
                        } catch (e) {
                          showError(e);
                        }
                      }}
                    >
                      {clans.my_clan.is_open ? t('privacyShortPrivate') : t('privacyShortPublic')}
                    </button>
                    <button
                      type="button"
                      className="action-btn stars soft-btn"
                      onClick={() => {
                        const link = clans.my_clan.invite_link;
                        if (link && isSafeTelegramUrl(link)) {
                          openSafeTelegramLink(
                            `https://t.me/share/url?url=${encodeURIComponent(link)}`
                          );
                        }
                        showInfo({
                          icon: 'handshake.png',
                          title: clans.my_clan.invite_code,
                          body: link,
                          confirmLabel: t('gotIt'),
                        });
                      }}
                    >
                      {t('clanInviteShort')}
                    </button>
                    <button
                      type="button"
                      className="action-btn stars soft-btn danger"
                      disabled={clanBusy}
                      onClick={async () => {
                        const ok = await showConfirm({
                          icon: 'handshake.png',
                          title: t('disbandClan'),
                          body: t('disbandConfirm'),
                          confirmLabel: t('disbandClan'),
                          cancelLabel: t('close'),
                          destructive: true,
                        });
                        if (!ok) return;
                        setClanBusy(true);
                        try {
                          setClans(await api.clanDisband());
                          showInfo({ icon: 'handshake.png', title: t('clanDisbanded'), confirmLabel: t('gotIt') });
                        } catch (e) {
                          showError(e);
                        } finally {
                          setClanBusy(false);
                        }
                      }}
                    >
                      {t('disbandShort')}
                    </button>
                  </div>
                ) : null}
                {clans.my_clan.role !== 'owner' ? (
                  <button
                    type="button"
                    className="action-btn stars soft-btn danger"
                    style={{ marginTop: 8 }}
                    disabled={clanBusy}
                    onClick={async () => {
                      const ok = await showConfirm({
                        icon: 'handshake.png',
                        title: t('leaveClan'),
                        body: t('leaveClanConfirm'),
                        confirmLabel: t('leaveClan'),
                        cancelLabel: t('close'),
                        destructive: true,
                      });
                      if (!ok) return;
                      const prev = clans;
                      setClans({ ...clans, my_clan: null });
                      setClanBusy(true);
                      try {
                        const res = await api.clanLeave();
                        setClans({ ...res, my_clan: null });
                      } catch (e) {
                        setClans(prev);
                        showError(e);
                      } finally {
                        setClanBusy(false);
                      }
                    }}
                  >
                    {t('leaveClan')}
                  </button>
                ) : (
                  <div className="item-desc" style={{ marginTop: 8 }}>
                    {t('ownerLeaveHint')}
                  </div>
                )}
              </div>
              <div className="section-label">{t('clanMembers')}</div>
              <div className="clan-members-list">
              {clans.my_clan.members?.map((m) => (
                <div className="frens-item clan-member soft-card" key={m.user_id}>
                  <span className="clan-member-left">
                    <Avatar userData={{ photo_url: m.photo_url, first_name: m.name }} size={36} />
                    <span className={`online-dot ${m.active_today ? 'on' : ''}`} />
                    <span className="clan-member-meta">
                      <span className="clan-member-name">
                        {m.name}
                        {m.role === 'owner' ? (
                          <span className="level-badge">{t('clanOwner')}</span>
                        ) : null}
                      </span>
                      <span className="clan-member-idle">{inactiveLabel(m, t)}</span>
                    </span>
                  </span>
                  <span className="clan-member-right">
                    <span className="clan-member-contrib">{m.contribution}</span>
                    {clans.my_clan.role === 'owner' && m.role !== 'owner' ? (
                      <div className="clan-member-actions">
                        <button
                          type="button"
                          className="action-btn soft-btn danger"
                          disabled={clanBusy}
                          onClick={async () => {
                            const ok = await showConfirm({
                              icon: 'handshake.png',
                              title: t('kickMember'),
                              body: t('kickConfirm', { n: m.name }),
                              confirmLabel: t('kickMember'),
                              cancelLabel: t('close'),
                              destructive: true,
                            });
                            if (!ok) return;
                            setClanBusy(true);
                            try {
                              setClans(await api.clanKick(m.user_id));
                            } catch (e) {
                              showError(e);
                            } finally {
                              setClanBusy(false);
                            }
                          }}
                        >
                          {t('kickShort')}
                        </button>
                        <button
                          type="button"
                          className="action-btn soft-btn"
                          disabled={clanBusy}
                          onClick={async () => {
                            const ok = await showConfirm({
                              icon: 'handshake.png',
                              title: t('transferClan'),
                              body: t('transferConfirm', { n: m.name }),
                              confirmLabel: t('transferClan'),
                              cancelLabel: t('close'),
                              destructive: true,
                            });
                            if (!ok) return;
                            setClanBusy(true);
                            try {
                              setClans(await api.clanTransfer(m.user_id));
                              showInfo({
                                icon: 'handshake.png',
                                title: t('clanTransferred'),
                                confirmLabel: t('gotIt'),
                              });
                            } catch (e) {
                              showError(e);
                            } finally {
                              setClanBusy(false);
                            }
                          }}
                        >
                          {t('transferShort')}
                        </button>
                      </div>
                    ) : null}
                  </span>
                </div>
              ))}
              </div>
              <div className="section-label">{t('topClans')}</div>
              {(clans.top || []).map((c) => (
                <div className={`upgrades-item soft-card clan-top-row ${c.id === clans.my_clan.id ? 'is-me' : ''}`} key={c.id}>
                  <span className={`rank-num ${c.rank <= 3 ? `podium-${c.rank}` : ''}`}>#{c.rank}</span>
                  <div className="item-text">
                    <span className="upgrades-item-info-name">
                      [{c.tag}] {c.name}
                      {c.id === clans.my_clan.id ? <span className="you-badge">{t('you')}</span> : null}
                    </span>
                    <span className="item-desc">
                      {t('power')} {c.power} · {c.members}/{c.max_members || 100}
                    </span>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              <div className="clan-gate soft-card">
                <div className="clan-gate-title">{t('clansGateTitle')}</div>
                <div className="item-desc">{t('clansGateBody')}</div>
                <div className="clan-gate-actions">
                  <button type="button" className="action-btn soft-btn clan-gate-btn" onClick={() => setClanSheet('create')}>
                    {t('createClan')}
                    <span className="clan-gate-cost">
                      <CoinIcon size={14} /> {clans.create_cost || 100}
                    </span>
                  </button>
                  <button type="button" className="action-btn stars soft-btn clan-gate-btn" onClick={() => setClanSheet('join')}>
                    {t('joinByCode')}
                  </button>
                </div>
              </div>
              <div className="section-label">{t('topClans')}</div>
              {clans.top?.map((c) => (
                <div className="upgrades-item soft-card" key={c.id}>
                  <span className={`rank-num ${c.rank <= 3 ? `podium-${c.rank}` : ''}`}>#{c.rank || '—'}</span>
                  <div className="item-text">
                    <span className="upgrades-item-info-name">
                      [{c.tag}] {c.name}
                      <span className="level-badge">
                        {c.is_open ? t('clanPublic') : t('clanPrivate')}
                      </span>
                    </span>
                    <span className="item-desc">
                      {t('power')} {c.power} · {c.members}/{c.max_members || 100}
                    </span>
                  </div>
                  {c.is_open ? (
                    <button
                      type="button"
                      className="action-btn soft-btn"
                      disabled={clanBusy}
                      onClick={async () => {
                        setClanBusy(true);
                        try {
                          setClans(await api.clanJoin(c.id));
                        } catch (e) {
                          showError(e);
                        } finally {
                          setClanBusy(false);
                        }
                      }}
                    >
                      {t('join')}
                    </button>
                  ) : (
                    <span className="btn-disabled">{t('clanPrivate')}</span>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      ) : null}

      {tab === 'prestige' && prestige ? (
        <div className="upgrades-list prestige-screen">
          <div className="invite-panel soft-card prestige-hero">
            <button
              type="button"
              className="prestige-help-btn"
              aria-label={t('prestigeHelpTitle')}
              onClick={() =>
                showInfo({
                  icon: 'glowing_star.png',
                  title: t('prestigeHelpTitle'),
                  body: t('prestigeHelpBody'),
                  confirmLabel: t('gotIt'),
                })
              }
            >
              ?
            </button>
            <FloatIcon src={iconUrl('glowing_star.png')} size={56} eager native animated />
            <div className="upgrades-item-info-name">
              {t('prestigeTitle')} ★{prestige.level}
            </div>
            <div className="prestige-mult">×{prestige.multiplier}</div>
            <div className="item-desc">
              {t('streak')} {prestige.streak?.current} ({t('best')} {prestige.streak?.best}) ·{' '}
              {t('seasonBonus')} {prestige.streak?.bonus_percent}% · {t('shields')}{' '}
              {prestige.streak?.shields}
            </div>
            <div className="item-desc prestige-need">
              {t('needCoinsForPrestige', { n: prestige.requirement })}
            </div>
            <button
              type="button"
              className="action-btn soft-btn prestige-cta"
              disabled={!prestige.can_prestige}
              onClick={doPrestige}
            >
              {t('doPrestige')}
            </button>
          </div>
        </div>
      ) : null}

      {clanSheet === 'create' ? (
        <AlertModal
          open
          icon="handshake.png"
          title={t('createClan')}
          body={t('createClanCost', { n: clans?.create_cost || 100 })}
          mode="confirm"
          confirmLabel={t('createClan')}
          cancelLabel={t('close')}
          busy={clanBusy}
          onClose={() => !clanBusy && setClanSheet(null)}
          onConfirm={async () => {
            if (clanBusy) return;
            if (!clanForm.name.trim() || !clanForm.tag.trim()) {
              showError({ message: t('clanNameTagRequired') });
              return;
            }
            setClanBusy(true);
            try {
              const drained = await awaitDrainPendingTaps();
              if (drained.last) mergeClicker(drained.last);
              if (!drained.ok) {
                showError({
                  message:
                    lang === 'en'
                      ? 'Still syncing taps — try again'
                      : 'Тапы ещё синхронизируются — попробуй ещё раз',
                });
                return;
              }
              const res = await api.clanCreate({
                name: clanForm.name.trim(),
                tag: clanForm.tag.trim(),
                description: clanForm.description.trim(),
                is_open: clanForm.is_open,
              });
              setClans(res);
              mergeClicker(await api.clickerSync());
              if (res?.amber != null) pushAmber(res.amber);
              setClanSheet(null);
              setClanForm({ name: '', tag: '', description: '', is_open: true });
            } catch (e) {
              showError(e);
            } finally {
              setClanBusy(false);
            }
          }}
        >
          <input
            className="hub-input"
            placeholder={t('clanName')}
            value={clanForm.name}
            onChange={(e) => setClanForm({ ...clanForm, name: e.target.value })}
          />
          <input
            className="hub-input"
            placeholder={t('clanTag')}
            maxLength={5}
            value={clanForm.tag}
            onChange={(e) => setClanForm({ ...clanForm, tag: e.target.value.toUpperCase() })}
          />
          <textarea
            className="hub-input clan-desc-input"
            placeholder={t('clanDesc')}
            maxLength={200}
            rows={3}
            value={clanForm.description}
            onChange={(e) => setClanForm({ ...clanForm, description: e.target.value })}
          />
          <label className="clan-check">
            <input
              type="checkbox"
              checked={clanForm.is_open}
              onChange={(e) => setClanForm({ ...clanForm, is_open: e.target.checked })}
            />
            {t('clanPublic')}
          </label>
        </AlertModal>
      ) : null}

      {clanSheet === 'join' ? (
        <AlertModal
          open
          icon="handshake.png"
          title={t('joinByCode')}
          body={t('joinByCodeBody')}
          mode="confirm"
          confirmLabel={t('join')}
          cancelLabel={t('close')}
          busy={clanBusy}
          onClose={() => !clanBusy && setClanSheet(null)}
          onConfirm={async () => {
            if (clanBusy || !inviteCode.trim()) return;
            setClanBusy(true);
            try {
              setClans(await api.clanJoinCode(inviteCode.trim()));
              setClanSheet(null);
              setInviteCode('');
            } catch (e) {
              showError(e);
            } finally {
              setClanBusy(false);
            }
          }}
        >
          <input
            className="hub-input"
            placeholder="CLANXXXXXXXX"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
          />
        </AlertModal>
      ) : null}

      <AlertModal
        open={Boolean(alert)}
        {...(alert || {})}
        onClose={closeAlert}
        onConfirm={alert?.mode === 'confirm' ? confirmAlert : closeAlert}
      />
    </main>
  );
};

export default Hub;
